import { useState, useEffect, useRef, useCallback } from "react";
import { ReclaimProofRequest } from "@reclaimprotocol/js-sdk";

// Simple theme hook for Create React App
const useTheme = () => {
  const [theme, setTheme] = useState('light');
  
  useEffect(() => {
    // Check for saved theme preference or default to light
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setTheme(savedTheme);
    } else {
      // Check system preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        setTheme('dark');
      }
    }
  }, []);

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  }, [theme]);

  return { 
    theme, 
    setTheme, 
    toggleTheme,
    resolvedTheme: theme 
  };
};

export function useReclaim({
  providerId,
  onComplete,
  onError,
}) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const [verifiedData, setVerifiedData] = useState(null);
  const [dataSourceInfo, setDataSourceInfo] = useState(null);
  const { resolvedTheme } = useTheme();
  const reclaimProofRequestRef = useRef(null);
  const onErrorRef = useRef(onError);
  const onCompleteRef = useRef(onComplete);

  // Update refs on each render to ensure they're always current
  onErrorRef.current = onError;
  onCompleteRef.current = onComplete;

  // Check if we have a provider ID
  const isSupported = !!providerId;
  const hasVerified = !!verifiedData;

  // This effect watches for the Reclaim modal and makes its overlay transparent.
  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    const observer = new MutationObserver((mutations, obs) => {
      const modalElement = document.getElementById('reclaim-qr-modal');
      if (modalElement) {
        modalElement.style.backgroundColor = 'transparent';
        obs.disconnect(); // Disconnect after finding and styling the modal
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // In case the modal is already present when this effect runs
    const existingModal = document.getElementById('reclaim-qr-modal');
    if (existingModal) {
      existingModal.style.backgroundColor = 'transparent';
      observer.disconnect();
    }

    return () => {
      observer.disconnect();
    };
  }, [isModalOpen]);

  // Effect to handle initialization and re-initialization when providerId changes.
  useEffect(() => {
    const initialize = async () => {
      // 1. Wait until we have a valid providerId.
      if (!providerId) {
        setIsInitialized(false);
        return; // Not an error, just waiting for config.
      }

      // 2. Start the initialization process.
      setVerificationError(null);
      setVerifiedData(null); // Reset verification data on re-init

      try {
        // Note: dataSourceInfo can be set by the parent component if needed
        // For now, we'll set it to null since we don't have the mock data registry

        const APP_ID = '0x991359364b67a3DCdB42EE03E5946f4c97aE139f';
const APP_SECRET = '0x64936dfd757fbea7b6011564475215a2c9c91d34a27d608fa42a3ed9af523970';
const PROVIDER_ID = 'example'; 

        const proofRequest = await ReclaimProofRequest.init(
          APP_ID,
          APP_SECRET,
          'example',
          {
            useBrowserExtension: false,
            useAppClip: true,
            customSharePageUrl: 'https://share.reclaimprotocol.org/vana'
          }
        );

        reclaimProofRequestRef.current = proofRequest;
        setIsInitialized(true);
        console.log("[useReclaim] SDK initialized for providerId:", providerId);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to initialize Reclaim";
        console.error("[useReclaim] Initialization failed:", errorMessage);
        setVerificationError(errorMessage);
        onErrorRef.current?.(errorMessage);
        setIsInitialized(false);
      }
    };

    // This effect will re-run whenever providerId changes, triggering re-initialization.
    initialize();

    // Cleanup function to reset state if the component unmounts.
    return () => {
      setIsInitialized(false);
      reclaimProofRequestRef.current = null;
    };
  }, [providerId, isSupported]);

  const handleVerificationSuccess = useCallback((proofs) => {
    try {
      console.log("[useReclaim] Verification successful:", proofs);
      const verifiedDataString = JSON.stringify(proofs, null, 2);

      setVerifiedData(proofs);
      setIsVerifying(false);
      setIsModalOpen(false);
      onCompleteRef.current?.(verifiedDataString);
    } catch (error) {
      handleVerificationError(error);
    }
  }, []);

  const handleVerificationError = useCallback((error) => {
    const errorMessage = error?.message || error || "Verification failed";
    console.error("[useReclaim] Verification error:", error);

    setVerificationError(errorMessage);
    setIsVerifying(false);
    setIsModalOpen(false);
    onErrorRef.current?.(errorMessage);
  }, []);

  const startVerification = useCallback(async () => {
    if (!isInitialized || !reclaimProofRequestRef.current) {
      const errorMessage =
        "Reclaim not initialized. Waiting for configuration.";
      console.error(
        "[useReclaim] startVerification called before initialization."
      );
      setVerificationError(errorMessage);
      onErrorRef.current?.(errorMessage);
      return;
    }

    try {
      setIsVerifying(true);
      setVerificationError(null);

      const proofReq = reclaimProofRequestRef.current;
      const url = await proofReq.getRequestUrl();
      
      // Check if we're in Flutter WebView context and handle appropriately
      if (typeof window !== 'undefined' && 'flutter_inappwebview' in window) {
        // Flutter WebView specific method
        window.flutter_inappwebview.callHandler('AppMessages', url);
      } else {
      
        function iOS() {
          return [
            'iPad Simulator',
            'iPhone Simulator', 
            'iPod Simulator',
            'iPad',
            'iPhone',
            'iPod'
          ].includes(navigator.platform)
          // iPad on iOS 13 detection
          || (navigator.userAgent.includes("Mac") && "ontouchend" in document)
        }
        
        // Get the true top window
        let topWindow = window;
        while (topWindow !== topWindow.parent) {
          topWindow = topWindow.parent;
        }
        
        // Desktop detection
        const isDesktop = !iOS() && !(/Android|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
        
        
if (isDesktop) {

          alert('desktop')
          proofReq.setModalOptions({
            title: "Verify Your Data",
            description:
              "Scan the QR code with your mobile device to verify your data securely",
            showExtensionInstallButton: false,
            modalPopupTimer: 10,
            darkTheme: resolvedTheme === "dark",
            onClose: () => {
              setIsModalOpen(false);
              // Only set isVerifying to false if we haven't already succeeded or failed
              if (isVerifying) {
                setIsVerifying(false);
              }
            },
          });
            // For desktop users, use triggerReclaimFlow instead of opening new tab (show qr modal)
          await proofReq.triggerReclaimFlow();
          setIsModalOpen(true);
        } else {
          setTimeout(() => {
            window.open(url, "_blank");
          })
        }
      }

      await proofReq.startSession({
        onSuccess: handleVerificationSuccess,
        onError: handleVerificationError,
      });
    } catch (error) {
      handleVerificationError(error);
    }
  }, [
    isInitialized,
    resolvedTheme,
    handleVerificationSuccess,
    handleVerificationError,
    isVerifying,
  ]);

  const resetError = useCallback(() => {
    setVerificationError(null);
  }, []);

  return {
    isInitialized,
    isVerifying,
    isModalOpen,
    verificationError,
    verifiedData,
    dataSourceInfo,
    isSupported,
    hasVerified,
    startVerification,
    resetError,
  };
} 